import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Menu,
  Note,
  Close,
  Person,
  VpnKey,
  ExitToApp,
  ExpandMore,
} from '@material-ui/icons';
import {
  Box,
  Link,
  Button,
  AppBar,
  Drawer,
  Hidden,
  MenuItem,
  Accordion,
  Container,
  Typography,
  AccordionSummary,
  AccordionDetails,
} from '@material-ui/core';

import { useScene } from '../../../hooks/useScene';
import { userActions } from '../../../actions';
import { ROUTER, NAV_ITEMS, APP_SCENE, ROLE } from '../../../constants';
import { isQcvFieldAdmin } from '../../../utils/permission';

import styles from './AppHeader.module.css';
import PopupMenu from '../../../components/CustomComponents/PopupMenu';
import tnvLogoDark from '../../../assets/image/app/tnv-logo-dark.png';


function AppHeader() {
  const scene = useScene();
  const dispatch = useDispatch();
  const navigate = useNavigate();


  const [openDrawer, setOpenDrawer] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(false);
  const [adminMenuAnchor, setAdminMenuAnchor] = useState(false);

  const feature = window.location.href;
  const user = useSelector(state => state.userReducer.user);

  const openUserMenu = (e) => setUserMenuAnchor(e.currentTarget);
  const closeUserMenu = () => setUserMenuAnchor(null);

  const openAdminMenu = (e) => setAdminMenuAnchor(e.currentTarget);
  const closeAdminMenu = () => setAdminMenuAnchor(null);

  const routeTo = (route) => {
    navigate(route);
    closeUserMenu();
    closeAdminMenu();
  }

  const logout = () => {
    dispatch(userActions.logout(navigate));
    closeUserMenu();
    closeAdminMenu();
  }

  const navItems = useMemo(() => {
    const res = [...NAV_ITEMS[APP_SCENE]];
    
    const role = user?.role;
    if (role) {
      const adminRole = APP_SCENE.toLowerCase() + '_admin_';
      
      if (scene.isQuaCauVang) {
        if (role === ROLE.SUPER_ADMIN || role === ROLE.COUNCIL || role === ROLE.COUNCIL_QCV) {
          // Có quyền truy cập vào Bình chọn
          res.push({ key: 'binh-chon', title: 'Bình chọn', to: ROUTER.BINH_CHON })
        }
       
        // Hội đồng theo lĩnh vực vào thẳng bằng mục nav này. super_admin và
        // quản trị viên cổng QCV đi qua menu "Quản trị viên" nên không thêm vào
        // đây, tránh hiện trùng 2 lần.
        if (isQcvFieldAdmin(role)) {
          res.push({ key: 'danh-sach-ho-so', title: 'Duyệt hồ sơ', to: ROUTER.DANH_SACH_HO_SO });
        }
      } else if (scene.isGiaiThuongNuSinh) {
        if (role === ROLE.SUPER_ADMIN || role === ROLE.COUNCIL || role === ROLE.COUNCIL_GTNS) {
          // Có quyền truy cập vào Bình chọn
          res.push({ key: 'binh-chon', title: 'Bình chọn', to: ROUTER.BINH_CHON })
        }

        if (role === ROLE.ADMIN_GTNS) {
          res.push({ key: 'danh-sach-ho-so', title: 'Duyệt hồ sơ', to: ROUTER.DANH_SACH_HO_SO });
        }
      } else if (scene.isKhueVanCac) {
        if (role === ROLE.SUPER_ADMIN || role === ROLE.COUNCIL || role === ROLE.COUNCIL_KVC) {
          // Có quyền truy cập vào Bình chọn
          res.push({ key: 'binh-chon', title: 'Bình chọn', to: ROUTER.BINH_CHON })
        }

        if (role === ROLE.COUNCIL || role === ROLE.COUNCIL_KVC) {
          res.push({ key: 'danh-sach-ho-so', title: 'Danh sách hồ sơ', to: ROUTER.DANH_SACH_HO_SO });
        }

        
        if (role.includes(adminRole)) {
          res.push({ key: 'danh-sach-ho-so', title: 'Duyệt hồ sơ', to: ROUTER.DANH_SACH_HO_SO });
        }
      }
    }


    return res;
  }, [APP_SCENE, user, scene]);

  const userMenu = [
    { key: 'thong-tin', title: 'Thông tin cá nhân', icon: <Person />, onClick: () => routeTo(ROUTER.THONG_TIN_CA_NHAN) },
    { key: 'ds-hs', title: 'Danh sách hồ sơ', icon: <Note />, onClick: () => routeTo(ROUTER.DANH_SACH_HO_SO_CA_NHAN) },
    { key: 'doi-mk', title: 'Đổi mật khẩu', icon: <VpnKey />, onClick: () => routeTo(ROUTER.DOI_MAT_KHAU) },
    { key: 'dang-xuat', title: 'Đăng xuất', icon: <ExitToApp />, onClick: logout },
  ];

  const isSuperAdmin = user?.role === ROLE.SUPER_ADMIN;
  // Quản trị viên riêng cổng QCV: chỉ hiện trên bản build QCV.
  const isPortalAdmin = scene.isQuaCauVang && user?.role === ROLE.ADMIN_QCV;
  // Ai được thấy menu "Quản trị viên" trên header.
  const showAdminMenu = isSuperAdmin || isPortalAdmin;

  // `superAdminOnly: true` = mục dùng chung cho cả 3 cổng (quản lý người dùng,
  // năm tổ chức) -> quản trị viên cổng QCV không được vào.
  const qcvAdminMenu = [
    { key: 'nam', title: 'Quản lý năm tổ chức', superAdminOnly: true, onClick: () => routeTo(ROUTER.QUAN_LY_NAM_TO_CHUC) },
    { key: 'danh-sach-ho-so', title: 'Danh sách hồ sơ', onClick: () => routeTo(ROUTER.DANH_SACH_HO_SO) },
    // Màn "Danh sách thành tích" (theo loại) chỉ có ở scene Quả Cầu Vàng -> ẩn ở KVC/GTNS để tránh 404
    ...(scene.isQuaCauVang
      ? [{ key: 'duyet-thanh-tich-ca-nhan', title: 'Danh sách thành tích', onClick: () => routeTo(ROUTER.QCV.DUYET_THANH_TICH_CA_NHAN.BANG_SANG_CHE) }]
      : []),
    { key: 'quan-ly-nguoi-dung', title: 'Quản lý người dùng', superAdminOnly: true, onClick: () => routeTo(ROUTER.QUAN_LY_NGUOI_DUNG) },
    { key: 'cau-hinh', title: 'Cấu hình Bảng tính điểm', onClick: () => routeTo(ROUTER.CAU_HINH) },
    // Trước đây chỉ vào được qua nút trong màn Bình chọn (mà quản trị viên cổng
    // QCV không có quyền vào) -> đưa hẳn vào menu quản trị.
    ...(scene.isQuaCauVang
      ? [{ key: 'ket-qua-binh-chon', title: 'Kết quả bình chọn', onClick: () => routeTo(ROUTER.QCV.KET_QUA_BINH_CHON) }]
      : []),
  ].filter((e) => isSuperAdmin || !e.superAdminOnly);

  return (
    <AppBar className={styles.appBar} position='fixed'>
      <Hidden smDown>
        <Container maxWidth="lg">
          <Box display="flex" height={64} alignItems='center' justifyContent='space-between'>
            <Box display='flex' alignItems='center'>
              <img alt='logo' src={tnvLogoDark} style={{ marginRight: 32, height: 52, marginTop: 4 }} />
              {navItems.map((e) => (
                <Link className={styles.routeButton} key={e.key} href={e.to}>{e.title}</Link>
              ))}
              {showAdminMenu && (
                <Link className={`${styles.routeButton} ${styles.adminButton}`} onClick={openAdminMenu}>
                  Quản trị viên
                  <svg className={styles.downIcon} viewBox='7 10 10 5'><path d='M7 10l5 5 5-5z' /></svg>
                </Link>
              )}
            </Box>
            <Box display='flex' alignItems='center' justifyContent='flex-end'>
              {user ? (
                <Box className={`${styles.routeButton} ${styles.userButton}`} onClick={openUserMenu}>
                  {user.username}
                  <svg className={styles.downIcon} viewBox='7 10 10 5'><path d='M7 10l5 5 5-5z' /></svg>
                </Box>
                ) : (
                  <>
                    <Link href={ROUTER.DANG_KY} className={styles.routeButton}>Đăng ký</Link>
                    <Button
                      color='secondary'
                      variant='contained'
                      href={ROUTER.DANG_NHAP}
                      style={{ float: 'right', margin: '10px 0 10px 16px' }}
                    >
                      Đăng nhập
                    </Button>
                  </>
                )}
            </Box>
          </Box>
        </Container>
      </Hidden>
      <Hidden mdUp>
        <Container maxWidth="xl">
          <Box display="flex" height={64} alignItems='center' justifyContent='space-between'>
            <Box display='flex' alignItems='center'>
              <img alt='logo' src={tnvLogoDark} style={{ marginRight: 32, height: 52, marginTop: 4 }} />
            </Box>
            <Box width='100%'>
              {openDrawer ? (
                <Close style={{ color: 'black', float: 'right' }} onClick={() => setOpenDrawer(false)} />
              ) : (
                <Menu style={{ color: 'black', float: 'right' }} onClick={() => setOpenDrawer(true)} />
              )}
            </Box>
          </Box>
        </Container>
      </Hidden>
      <PopupMenu
        position='right'
        anchorEl={userMenuAnchor}
        handleClose={closeUserMenu}
        render={
          <>
            {userMenu.map((e) => (
              <MenuItem key={e.key} onClick={e.onClick}>
                {e.icon}
                <Box className={styles.userMenu}>{e.title}</Box>
              </MenuItem>
            ))}
          </>
        } 
      />

      <PopupMenu
        position='center'
        anchorEl={adminMenuAnchor}
        disableRestoreFocus
        handleClose={closeAdminMenu}
        render={
          <>
            {qcvAdminMenu.map((e) => (
              <MenuItem key={e.key} onClick={e.onClick} style={{ fontWeight: 'bold' }}>
                {e.title}
              </MenuItem>
            ))}
          </>
        } 
      />

      <Drawer
        anchor='top'
        open={openDrawer}
        PaperProps={{ style: { marginTop: 64, color: 'black' } }}
        onClose={() => setOpenDrawer(false)}
        style={{ zIndex: 2 }}
      >
        {navItems.map((e) => (
          <Link
            key={e.key}
            href={e.to}
            className={styles.mobileRouteButton}
            style={{
              borderLeftColor: feature.includes(e.to) ? 'black' : '#ffffff00',
              backgroundColor: feature.includes(e.to) ? '#00000040' : '#ffffff00',
            }}
          >
            {e.title}
          </Link>
        ))}
        {showAdminMenu && (
          <Accordion className={styles.mobileUserCollapse} TransitionProps={{ style: { marginTop: 0} }}>
            <AccordionSummary className={styles.mobileUser} expandIcon={<ExpandMore />}>
              <Typography><b>Quản trị viên</b></Typography>
            </AccordionSummary>
            <AccordionDetails className={styles.mobileUserMenu}>
              <Box width='100%'>
                {qcvAdminMenu.map((e) => (
                  <MenuItem key={e.key} onClick={() => { e.onClick(); setOpenDrawer(false); }} style={{ height: 56 }}>
                    <Typography variant='subtitle1' style={{ marginLeft: 16, fontWeight: 600 }}>{e.title}</Typography>
                  </MenuItem>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
        {user ? (
          <Accordion className={styles.mobileUserCollapse} TransitionProps={{ style: { marginTop: 0} }}>
            <AccordionSummary className={styles.mobileUser} expandIcon={<ExpandMore />}>
              <Typography style={{ fontWeight: 600 }}>{user.username}</Typography>
            </AccordionSummary>
            <AccordionDetails className={styles.mobileUserMenu}>
              <Box width='100%'>
                {userMenu.map((e) => (
                  <MenuItem key={e.key} onClick={() => { e.onClick(); setOpenDrawer(false); }} style={{ height: 56 }}>
                    {e.icon}
                    <Typography variant='subtitle1' style={{ marginLeft: 16, fontWeight: 600 }}>{e.title}</Typography>
                  </MenuItem>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        ) : (
          <Box style={{ marginLeft: 20, lineHeight: '56px' }} onClick={() => { setOpenDrawer(false); navigate(ROUTER.DANG_NHAP); }}>
            <b>Đăng nhập</b>
          </Box>
        )}
      </Drawer>
    </AppBar>
  )
}

export default AppHeader;
